import { Box, Text } from '@youversion/design-system-components';
import React, { type JSX } from 'react';

interface Props extends React.HTMLProps<HTMLDivElement> {
  className?: string;
  id?: string;
  label?: string;
  onClick?: () => void;
  rightElement?: JSX.Element;
  rightText?: string;
  secondaryLabel?: string;
  secondaryTitle?: string;
  startIcon?: JSX.Element;
  title?: string;
}

/**
 * Option for give list.
 *
 * @param className - Class name(s) to apply.
 * @param id - The value to set for the `id` property.
 * @param label - Text used for the label.
 * @param onClick - The callback handler for the click event.
 * @param rightElement - Element to show on the right side.
 * @param secondaryLabel - Label used for the secondary action.
 * @param secondaryTitle - Value for the secondary action.
 * @param startIcon - Icon that goes on the start.
 * @param title - Tells the title of the item. I.e. Frequency / Process Date etc.
 * @param props - Customization options passed to container.
 * @returns React.ReactNode.
 *
 * @example
 *
 * ```typescriptreact
 * <GiveOptionListItem
 *  onClick={() => {}}
 *  secondaryLabel="Process Gift On"
 *  secondaryValue="Today"
 * />
 * ```
 */
export default function GiveOptionListItem({
  label,
  onClick,
  rightElement,
  rightText,
  secondaryLabel,
  secondaryTitle,
  startIcon,
  title,
  ...props
}: Props) {
  return (
    <div tabIndex={0} {...props} onClick={onClick}>
      <Box className='relative'>
        <Box className='flex justify-between items-center cursor-pointer'>
          <Box className='flex items-center'>
            {startIcon ? <Box className='m-0'>{startIcon}</Box> : null}
            <Text size='md' variant='caption'>
              {title}
            </Text>
            <Box className='m-1'>
              <Text className='mie-1' size='lg' variant='label'>
                {label}
              </Text>
            </Box>
          </Box>
          {rightText ? (
            <Box className='flex items-center mie-4'>
              <Text size='md' variant='label'>
                {rightText}
              </Text>
            </Box>
          ) : null}
        </Box>
        {secondaryLabel ? (
          <div
            className='mbe-1 rounded-0.5 flex justify-between items-center space-x-0 cursor-pointer mie-2'
            data-testid='secondary'
            tabIndex={0}
          >
            <Box className='flex items-start'>
              <Box className='max-w-sm'>
                <Text className='mis-1' size='md' variant='caption'>
                  {secondaryLabel}
                </Text>
                {secondaryTitle ? (
                  <Text size='md' variant='caption'>
                    {secondaryTitle}
                  </Text>
                ) : null}
              </Box>
            </Box>
          </div>
        ) : null}
        {rightElement ? (
          <div className='absolute rtl:left-0 ltr:right-0 top-1/2 -translate-y-1/2'>{rightElement}</div>
        ) : null}
      </Box>
    </div>
  );
}
